// Error types
export const ERROR_TYPES = {
  NETWORK: "NETWORK",
  VALIDATION: "VALIDATION",
  AUTHENTICATION: "AUTHENTICATION",
  AUTHORIZATION: "AUTHORIZATION",
  SERVER: "SERVER",
  TIMEOUT: "TIMEOUT",
  UNKNOWN: "UNKNOWN",
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
};

class ErrorHandler {
  constructor() {
    this.retryAttempts = new Map();
    this.maxRetries = 3;
    this.retryDelay = 1000; // Base delay in ms
    this.offlineQueue = [];
    this.isOnline = navigator.onLine;

    // Listen for online/offline events
    window.addEventListener("online", this.handleOnline.bind(this));
    window.addEventListener("offline", this.handleOffline.bind(this));
  }

  /**
   * Main error handling method
   */
  handleError(error, context = {}) {
    const errorInfo = this.parseError(error);
    const errorId = this.generateErrorId();

    const enrichedError = {
      ...errorInfo,
      id: errorId,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Log error
    this.logError(enrichedError);

    // Show user notification
    this.showUserNotification(enrichedError);

    return enrichedError;
  }

  /**
   * Parse error to determine type and extract useful information
   */
  parseError(error) {
    let errorType = ERROR_TYPES.UNKNOWN;
    let severity = ERROR_SEVERITY.MEDIUM;
    let message = "An unexpected error occurred";
    let canRetry = false;
    let userAction = null;

    if (error.response) {
      // HTTP error response
      const status = error.response.status;
      const data = error.response.data;

      if (status >= 400 && status < 500) {
        errorType =
          status === 401
            ? ERROR_TYPES.AUTHENTICATION
            : status === 403
              ? ERROR_TYPES.AUTHORIZATION
              : ERROR_TYPES.VALIDATION;
        severity = ERROR_SEVERITY.MEDIUM;
        message = data?.message || `Request failed with status ${status}`;
        userAction = this.getValidationAction(status, data);
      } else if (status >= 500) {
        errorType = ERROR_TYPES.SERVER;
        severity = ERROR_SEVERITY.HIGH;
        message = "Server error occurred. Please try again later.";
        canRetry = true;
        userAction =
          "Please try again in a few moments. If the problem persists, contact support.";
      }
    } else if (error.request) {
      // Network error
      errorType = ERROR_TYPES.NETWORK;
      severity = ERROR_SEVERITY.HIGH;
      message = "Network connection failed";
      canRetry = true;
      userAction = this.isOnline
        ? "Please check your internet connection and try again."
        : "You appear to be offline. Your data will be saved and synced when connection is restored.";
    } else if (error.code === "TIMEOUT") {
      errorType = ERROR_TYPES.TIMEOUT;
      severity = ERROR_SEVERITY.MEDIUM;
      message = "Request timed out";
      canRetry = true;
      userAction = "The request took too long. Please try again.";
    }

    return {
      type: errorType,
      severity,
      message,
      originalError: error,
      canRetry,
      userAction,
    };
  }

  /**
   * Get user action suggestions for validation errors
   */
  getValidationAction(status, data) {
    if (status === 401) {
      return "Please log in again to continue.";
    }
    if (status === 403) {
      return "You do not have permission to perform this action.";
    }
    if (status === 422 && data?.errors) {
      return `Please correct the following: ${data.errors.join(", ")}`;
    }
    return "Please check your input and try again.";
  }

  /**
   * Retry mechanism with exponential backoff
   */
  async retryRequest(requestFn, context = {}, customMaxRetries = null) {
    const maxRetries = customMaxRetries || this.maxRetries;
    const retryKey = context.retryKey || "default";

    if (!this.retryAttempts.has(retryKey)) {
      this.retryAttempts.set(retryKey, 0);
    }

    const attempts = this.retryAttempts.get(retryKey);

    try {
      const result = await requestFn();
      this.retryAttempts.delete(retryKey); // Clear on success
      return result;
    } catch (error) {
      if (attempts < maxRetries && this.shouldRetry(error)) {
        this.retryAttempts.set(retryKey, attempts + 1);
        const delay = this.calculateDelay(attempts);

        console.log(
          `Retrying request (attempt ${attempts + 1}/${maxRetries}) after ${delay}ms delay`,
        );

        await this.delay(delay);
        return this.retryRequest(requestFn, context, customMaxRetries);
      } else {
        this.retryAttempts.delete(retryKey);
        throw error;
      }
    }
  }

  /**
   * Determine if error should trigger a retry
   */
  shouldRetry(error) {
    if (!this.isOnline) return false;

    const status = error.response?.status;
    return !status || status >= 500 || status === 408 || status === 429;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  calculateDelay(attempt) {
    const jitter = Math.random() * 0.1 * this.retryDelay;
    return Math.min(this.retryDelay * Math.pow(2, attempt) + jitter, 30000);
  }

  /**
   * Queue requests for when connection is restored
   */
  queueForRetry(requestFn, context = {}) {
    if (!this.isOnline) {
      this.offlineQueue.push({ requestFn, context, timestamp: Date.now() });
      this.showOfflineNotification();
      return Promise.resolve({ queued: true });
    }
    return requestFn();
  }

  /**
   * Handle online event
   */
  async handleOnline() {
    this.isOnline = true;
    console.log("Connection restored, processing queued requests...");

    const queuedRequests = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const { requestFn, context } of queuedRequests) {
      try {
        await this.retryRequest(requestFn, context);
      } catch (error) {
        this.handleError(error, { ...context, fromQueue: true });
      }
    }

    if (queuedRequests.length > 0) {
      this.showNotification(
        "Connection restored! Your data has been synced.",
        "success",
      );
    }
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.isOnline = false;
    this.showOfflineNotification();
  }

  /**
   * Show offline notification
   */
  showOfflineNotification() {
    this.showNotification(
      "You are currently offline. Your changes will be saved and synced when connection is restored.",
      "warning",
      { persistent: true },
    );
  }

  /**
   * Show user notification based on error
   */
  showUserNotification(errorInfo) {
    const { severity, message, userAction } = errorInfo;

    let notificationType = "error";
    if (severity === ERROR_SEVERITY.LOW) notificationType = "info";
    else if (severity === ERROR_SEVERITY.MEDIUM) notificationType = "warning";

    const fullMessage = userAction ? `${message}. ${userAction}` : message;

    this.showNotification(fullMessage, notificationType, {
      duration: severity === ERROR_SEVERITY.CRITICAL ? 0 : 5000,
    });
  }

  /**
   * Show notification (can be overridden to integrate with UI library)
   */
  showNotification(message, type = "info", options = {}) {
    // Default implementation - can be overridden
    console.log(`[${type.toUpperCase()}] ${message}`);

    // Create simple toast notification
    const toast = document.createElement("div");
    toast.className = `error-toast error-toast--${type}`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 4px;
      color: white;
      z-index: 10000;
      max-width: 400px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      background: ${this.getToastColor(type)};
    `;

    document.body.appendChild(toast);

    if (!options.persistent) {
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, options.duration || 5000);
    }
  }

  /**
   * Get toast color based on type
   */
  getToastColor(type) {
    const colors = {
      error: "#dc3545",
      warning: "#fd7e14",
      success: "#198754",
      info: "#0dcaf0",
    };
    return colors[type] || colors.info;
  }

  /**
   * Log error for monitoring
   */
  logError(errorInfo) {
    // Console logging for development
    console.group(`🚨 Error [${errorInfo.type}] - ${errorInfo.severity}`);
    console.error("Message:", errorInfo.message);
    console.error("Context:", errorInfo.context);
    console.error("Original Error:", errorInfo.originalError);
    console.error("Error Info:", errorInfo);
    console.groupEnd();

    // Send to monitoring service (implement based on your monitoring solution)
    this.sendToMonitoring(errorInfo);
  }

  /**
   * Send error to monitoring service
   */
  sendToMonitoring(errorInfo) {
    // Example implementation - replace with your monitoring service
    // Check if we're in production using window.location
    const isProduction = window.location.hostname !== 'localhost' && 
                         window.location.hostname !== '127.0.0.1';
    
    if (isProduction) {
      // Example: Sentry, LogRocket, or custom endpoint
      console.log("Error monitoring:", errorInfo);
      // Sentry.captureException(errorInfo.originalError, {
      //   tags: { type: errorInfo.type },
      //   extra: errorInfo
      // });
    }
  }

  /**
   * Generate unique error ID
   */
  generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Utility delay function
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Clear retry state for a specific key
   */
  clearRetryState(retryKey) {
    this.retryAttempts.delete(retryKey);
  }

  /**
   * Get current retry count
   */
  getRetryCount(retryKey) {
    return this.retryAttempts.get(retryKey) || 0;
  }
}

// Export singleton instance
export const errorHandler = new ErrorHandler();

// Utility functions for common scenarios
export const withErrorHandling = (fn, context = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      return errorHandler.handleError(error, context);
    }
  };
};

export const withRetry = (fn, context = {}) => {
  return () => errorHandler.retryRequest(fn, context);
};

export const queueIfOffline = (fn, context = {}) => {
  return () => errorHandler.queueForRetry(fn, context);
};
